import {
  DocumentDeletionBlockReason,
  ProjectDocument,
  ProjectElement,
  ProjectFolder,
} from 'services/api/api-client.types';

export enum NodeType {
  Folder = 'Folder',
  Document = 'Document',
  Template = 'Template',
}

export const isFolder = (node: BaseNode): node is BaseFolder =>
  node.type === NodeType.Folder;

export const isFile = (node: BaseNode): node is BaseFile =>
  node.type === NodeType.Document || node.type === NodeType.Template;

export type BaseNode = {
  id: string;
  type: NodeType;
  name: string;
  disableSorting?: boolean;
  canHaveChildren?: boolean;
  onDelete?: (id: string) => Promise<void>;
  canBeDeleted?: boolean;
};

function isProjectFolder(element: ProjectElement): element is ProjectFolder {
  return element.type === 'Folder';
}

function isProjectDocument(
  element: ProjectElement,
): element is ProjectDocument {
  return element.type === 'Document';
}

export function parseProjectElements(
  folders: ProjectElement[],
  fileType: NodeType.Document | NodeType.Template,
): BaseNode[] {
  const parse = (elem: ProjectElement): BaseFile | BaseFolder<BaseNode> => {
    if (isProjectFolder(elem)) {
      const children = elem.children.map(parse) as BaseNode[];
      return {
        id: elem.id,
        type: NodeType.Folder,
        name: elem.name,
        children,
        collapsed: elem.collapsed,
        canBeDeleted: true,
        onAddSubItem: () => Promise.resolve(),
        onFolderEdit: () => Promise.resolve(),
      };
    } else if (isProjectDocument(elem)) {
      return {
        id: elem.id,
        documentId: elem.documentId,
        type: fileType,
        name: elem.name,
        isExported: false,
      };
    } else {
      throw new Error('Unknown project element type');
    }
  };

  return folders.map(parse);
}

export function cloneNodes<T extends BaseNode>(node: T[]): T[] {
  return JSON.parse(JSON.stringify(node));
}

export type BaseFile = {
  isExported: boolean;
  documentId: number;
  deletionBlockReason?: DocumentDeletionBlockReason;
} & BaseNode;

export enum FolderDeletionBlockReason {
  HasBlockedChildren = 'HasBlockedChildren',
}

export type BaseFolder<T extends BaseNode = BaseNode> = {
  type: NodeType.Folder;
  children: T[];
  collapsed: boolean;
  onAddSubItem: (folderId: string, node: T) => Promise<void>;
  onFolderEdit: (
    folderId: string,
    name: string,
    editedProperty: any,
  ) => Promise<void>;
  deletionBlockReason?: FolderDeletionBlockReason;
} & BaseNode;

export function getAllDescendants<T extends BaseNode>(node: T): BaseNode[] {
  if (!isFolder(node)) return [];
  return node.children.flatMap((child) => [child, ...getAllDescendants(child)]);
}
